(function (global) {
  const ARTICLE_RTE_SELECTOR = 'div[id*="__main-article"] .rte, article.article .rte, .article--content';
  const CUSTOM_EMBED_TAGS = new Set(['lite-youtube', 'video-cover', 'cued-overlay']);
  const DEFAULT_BASE_URL = 'https://example.com';

  function matchesCustomEmbed(element) {
    if (!element?.tagName) return false;

    const tagName = element.tagName.toLowerCase();
    return tagName.startsWith('ytm-') || tagName.startsWith('ytw-') || CUSTOM_EMBED_TAGS.has(tagName);
  }

  function parseYouTubeTime(value) {
    if (!value) return null;
    if (/^\d+$/.test(value)) return value;

    const match = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/i);
    if (!match) return null;

    const hours = Number(match[1] || 0);
    const minutes = Number(match[2] || 0);
    const seconds = Number(match[3] || 0);
    const totalSeconds = (hours * 3600) + (minutes * 60) + seconds;

    return totalSeconds > 0 ? String(totalSeconds) : null;
  }

  function buildUrl(source) {
    if (!source) return null;

    try {
      const baseUrl = global?.location?.origin || DEFAULT_BASE_URL;
      return new URL(source, baseUrl);
    } catch (_error) {
      return null;
    }
  }

  function buildYouTubeEmbedSrc(source) {
    const url = buildUrl(source);
    if (!url) return null;

    const host = url.hostname.replace(/^www\./, '');
    const isYouTubeHost = host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com' || host === 'youtube-nocookie.com';
    const isShortHost = host === 'youtu.be';

    if (!isYouTubeHost && !isShortHost) return null;

    let videoId = '';
    if (isShortHost) {
      videoId = url.pathname.replace(/^\/+/, '').split('/')[0];
    } else if (url.pathname === '/watch') {
      videoId = url.searchParams.get('v') || '';
    } else if (url.pathname.startsWith('/shorts/')) {
      videoId = url.pathname.split('/')[2] || '';
    } else if (url.pathname.startsWith('/embed/')) {
      return url.toString();
    }

    if (!videoId) return null;

    const embedUrl = new URL(`https://www.youtube.com/embed/${videoId}`);
    embedUrl.searchParams.set('playsinline', '1');
    embedUrl.searchParams.set('rel', '0');

    const start = parseYouTubeTime(url.searchParams.get('t') || url.searchParams.get('start'));
    if (start) {
      embedUrl.searchParams.set('start', start);
    }

    const end = url.searchParams.get('end');
    if (end) {
      embedUrl.searchParams.set('end', end);
    }

    const list = url.searchParams.get('list');
    if (list) {
      embedUrl.searchParams.set('list', list);
    }

    return embedUrl.toString();
  }

  function buildVimeoEmbedSrc(source) {
    const url = buildUrl(source);
    if (!url) return null;

    const host = url.hostname.replace(/^www\./, '');
    if (host !== 'vimeo.com' && host !== 'player.vimeo.com') return null;

    const pathParts = url.pathname.split('/').filter(Boolean);
    const videoId = pathParts[pathParts[0] === 'video' ? 1 : 0];
    if (!videoId || !/^\d+$/.test(videoId)) return null;

    const embedUrl = new URL(`https://player.vimeo.com/video/${videoId}`);
    embedUrl.searchParams.set('autopause', '1');
    embedUrl.searchParams.set('responsive', '1');
    embedUrl.searchParams.set('portrait', '0');
    embedUrl.searchParams.set('title', '0');
    embedUrl.searchParams.set('byline', '0');

    return embedUrl.toString();
  }

  function normalizeVideoSource(source) {
    return buildYouTubeEmbedSrc(source) || buildVimeoEmbedSrc(source);
  }

  function extractYouTubeIdFromThumbnailUrl(value) {
    if (!value) return null;

    const match = String(value).match(/(?:ytimg\.com\/(?:vi|vi_webp)\/|img\.youtube\.com\/vi\/)([A-Za-z0-9_-]{6,})/i);
    return match ? match[1] : null;
  }

  function extractSrcsetUrl(value) {
    if (!value) return null;

    const firstEntry = String(value).split(',')[0];
    return firstEntry ? firstEntry.trim().split(/\s+/)[0] : null;
  }

  function extractEmbedSourceFromElement(element) {
    if (!element) return null;

    const directHref = element.getAttribute?.('href');
    const directSource = normalizeVideoSource(directHref);
    if (directSource) return directSource;

    if (typeof element.querySelectorAll === 'function') {
      const links = Array.from(element.querySelectorAll('a[href]'));
      for (const link of links) {
        const embedSource = normalizeVideoSource(link.getAttribute('href'));
        if (embedSource) return embedSource;
      }

      const mediaNodes = Array.from(element.querySelectorAll('[style], img[src], source[srcset]'));
      for (const mediaNode of mediaNodes) {
        const thumbnailId =
          extractYouTubeIdFromThumbnailUrl(mediaNode.getAttribute('style')) ||
          extractYouTubeIdFromThumbnailUrl(mediaNode.getAttribute('src')) ||
          extractYouTubeIdFromThumbnailUrl(extractSrcsetUrl(mediaNode.getAttribute('srcset')));

        if (thumbnailId) {
          return buildYouTubeEmbedSrc(`https://www.youtube.com/watch?v=${thumbnailId}`);
        }
      }
    }

    return null;
  }

  function resolveAspectRatio(element) {
    const width = Number(element.getAttribute?.('width'));
    const height = Number(element.getAttribute?.('height'));

    if (width > 0 && height > 0) {
      return `${width} / ${height}`;
    }

    return null;
  }

  function createEmbedWrapper(documentRef, aspectRatio) {
    const wrapper = documentRef.createElement('div');
    wrapper.className = 'article__embed';

    if (aspectRatio) {
      wrapper.style.setProperty('--article-embed-aspect-ratio', aspectRatio);
    }

    return wrapper;
  }

  function createIframe(documentRef, source) {
    const iframe = documentRef.createElement('iframe');
    iframe.src = source;
    iframe.loading = 'lazy';
    iframe.allowFullscreen = true;
    iframe.setAttribute('allowfullscreen', 'allowfullscreen');
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');

    return iframe;
  }

  function wrapEmbed(element) {
    if (!element || element.closest('.article__embed')) return;

    const wrapper = createEmbedWrapper(element.ownerDocument, resolveAspectRatio(element));
    element.parentNode.insertBefore(wrapper, element);
    wrapper.appendChild(element);
  }

  function replaceElementWithEmbed(element) {
    if (!element?.ownerDocument || typeof element.replaceWith !== 'function') return false;

    const embedSource = extractEmbedSourceFromElement(element);
    if (!embedSource) return false;

    const wrapper = createEmbedWrapper(element.ownerDocument, resolveAspectRatio(element));
    const iframe = createIframe(element.ownerDocument, embedSource);
    wrapper.appendChild(iframe);
    element.replaceWith(wrapper);

    return true;
  }

  function normalizeIframe(iframe) {
    const normalizedSrc = normalizeVideoSource(iframe.getAttribute('src'));
    if (normalizedSrc) {
      iframe.src = normalizedSrc;
    }

    iframe.setAttribute('loading', iframe.getAttribute('loading') || 'lazy');
    iframe.setAttribute('allowfullscreen', 'allowfullscreen');
    iframe.setAttribute('referrerpolicy', iframe.getAttribute('referrerpolicy') || 'strict-origin-when-cross-origin');

    if (!iframe.getAttribute('allow')) {
      iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
    }
  }

  function collectCustomEmbeds(container) {
    return Array.from(container.querySelectorAll('*')).filter((element) => {
      if (!matchesCustomEmbed(element)) return false;

      const parent = element.parentElement;
      if (!parent || parent.classList.contains('article__embed')) return true;

      return !parent.closest('.article__embed') && !matchesCustomEmbed(parent);
    });
  }

  function looksLikeVideoPreviewLink(link) {
    if (!link?.querySelector) return false;

    return Boolean(
      link.querySelector('img, picture, svg, lite-youtube, video-cover, [style*="ytimg.com"], [style*="img.youtube.com"]')
    );
  }

  function collectPreviewLinks(container) {
    return Array.from(container.querySelectorAll('a[href]')).filter((link) => {
      if (link.closest('.article__embed')) return false;
      if (!normalizeVideoSource(link.getAttribute('href'))) return false;

      return looksLikeVideoPreviewLink(link);
    });
  }

  function normalizeArticleEmbeds() {
    if (typeof document === 'undefined') return;

    const rteContainers = document.querySelectorAll(ARTICLE_RTE_SELECTOR);
    if (rteContainers.length === 0) return;

    rteContainers.forEach((container) => {
      container.querySelectorAll('iframe, embed, object').forEach((element) => {
        if (element.tagName.toLowerCase() === 'iframe') {
          normalizeIframe(element);
        }

        wrapEmbed(element);
      });

      collectCustomEmbeds(container).forEach((element) => {
        if (!replaceElementWithEmbed(element)) {
          wrapEmbed(element);
        }
      });

      collectPreviewLinks(container).forEach((link) => {
        replaceElementWithEmbed(link);
      });
    });
  }

  const api = {
    buildYouTubeEmbedSrc,
    extractYouTubeIdFromThumbnailUrl,
    extractEmbedSourceFromElement,
    normalizeArticleEmbeds
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  global.ArticleVideoEmbeds = api;

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', normalizeArticleEmbeds);
    } else {
      normalizeArticleEmbeds();
    }
  }
})(typeof window !== 'undefined' ? window : globalThis);
